import angular from 'angular'

/**
 * Directive to trigger a tooltip on hover/touch of the element. The attribute value can either be the plain text
 * string to display as the tooltip content or an object containing the options to pass to vdTooltip.open().
 *
 * Usage:
 *
 * <a vd-tooltip-trigger="'Hello World!'">hello?</a>
 *
 * <a vd-tooltip-trigger="{{$ctrl.tooltipText}}">hello?</a>
 *
 * <a vd-tooltip-trigger="{ template: '<vd-tooltip>Hello <strong>World</strong>!' }">hello?</a>
 *
 * <a vd-tooltip-trigger="$ctrl.tooltipOptions">hello?</a>
 *
 * Options:
 *
 * @param {String} tooltipText
 *        The plain text to display in the tooltip
 *
 * @param {Boolean} [showOnTouch = false]
 *        Whether to display the tooltip on touch events (default is only on mouseenter)
 *
 * @param {Number} [delay = 200]
 *        Number of milliseconds to delay after the trigger event before showing the tooltip
 */
export default function TooltipTriggerDirective($timeout, vdTooltip) {
  'ngInject'
  const DEFAULT_DELAY = 200

  return {
    restrict: 'A',
    link($scope, $element, $attrs) {
      let touchTriggered = false
      let timer

      /**
       * Open the tooltip with the given options.
       *
       * @method openTooltip
       *
       * @param {Object} options
       */
      function openTooltip(options) {
        const delay = angular.isNumber(options.delay)
          ? options.delay
          : DEFAULT_DELAY

        if (delay > 0) {
          if (timer) {
            $timeout.cancel(timer)
          }
          timer = $timeout(() => vdTooltip.open(options), delay)
        } else {
          vdTooltip.open(options)
        }
      }

      $element.on('touchstart', () => {
        touchTriggered = true
      })

      $element.on('mouseenter', event => {
        const tooltipConfig = $scope.$eval($attrs.vdTooltipTrigger)

        if (!touchTriggered || tooltipConfig.showOnTouch) {
          const options =
            typeof tooltipConfig === 'string'
              ? { tooltipText: tooltipConfig }
              : tooltipConfig
          options.trigger = event.currentTarget

          openTooltip(options)
        }

        touchTriggered = false
      })

      $element.on('mouseleave', () => {
        if (timer) {
          $timeout.cancel(timer)
          timer = null
        }
        vdTooltip.close()
      })
    },
  }
}
